import { useState, useEffect } from 'react';
import {
  Plus,
  Download,
  LayoutDashboard,
  UserCog,
  SlidersHorizontal,
  ArrowLeft,
  FolderOpen,
  AlarmClock,
  HeartPulse,
  UserPlus,
  UserRound,
  Mail,
  BadgeCheck,
  ChevronDown,
  User,
  X,
  Phone,
  Search,
  Check,
} from 'lucide-react';
import AttentionCard from './components/AttentionCard';
import KpiGrid from './components/KpiGrid';
import ThroughputCard from './components/ThroughputCard';
// RISK — COMMENTED OUT (code kept, not deleted): Risk Mix card is disabled.
// import RiskMixCard from './components/RiskMixCard';
import FunnelCard from './components/FunnelCard';
import AlertBanner from './components/AlertBanner';
import WidgetPermissions from './components/WidgetPermissions';
import { ROLES, loadVisibility, saveVisibility } from './data/widgets';
import InviteCustomerModal from '../People-Management/invite-modal';
import { useNavigate } from 'react-router-dom';

const COUNTRY_CODES = [
  { code: '+61', label: 'AU', flag: '🇦🇺' },
  { code: '+64', label: 'NZ', flag: '🇳🇿' },
  { code: '+44', label: 'UK', flag: '🇬🇧' },
  { code: '+1', label: 'US', flag: '🇺🇸' },
  { code: '+91', label: 'IN', flag: '🇮🇳' },
  { code: '+65', label: 'SG', flag: '🇸🇬' },
];
const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

const today = () =>
  new Date().toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

export default function BusinessDashboard() {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();

  const [view, setView] = useState('dashboard'); // dashboard | permissions
  const [inviteOpen, setInviteOpen] = useState(false);
  const CAT_FOR_TYPE = (type) => {
    const t = type.toLowerCase();
    if (t.includes('consultant')) return 'trust';
    if (t.includes('owner') || t.includes('compliance')) return 'corporate';
    return 'individual';
  };
  const [toast, setToast] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const addCustomer = (form) => {
    const fullName = `${form.firstName} ${form.lastName}`.trim();
    const type = form.appType;
    const category = CAT_FOR_TYPE(type);
    const typeKind = type.toLowerCase().includes('owner')
      ? 'owner'
      : type.toLowerCase().includes('compliance')
        ? 'compliance'
        : type.toLowerCase().includes('trainer')
          ? 'trainer'
          : type.toLowerCase().includes('manager')
            ? 'trained'
            : type.toLowerCase().includes('consultant')
              ? 'consultant'
              : 'officer';
    const initials = (form.firstName[0] || '') + (form.lastName[0] || '');

    setToast(`Invite sent to “${fullName}”`);
    setTimeout(() => setToast(null), 2600);
  };

  const [inviteForm, setInviteForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    country: '+61',
    applicationType: 'Agency Compliance Officer',
  });
  const [role, setRole] = useState(
    () => localStorage.getItem('demoRole') || 'agency',
  );
  const [visibility, setVisibility] = useState(() => loadVisibility());

  const handleRoleChange = (r) => {
    setRole(r);
    localStorage.setItem('demoRole', r);
  };

  const handleInviteFieldChange = (field) => (event) => {
    setInviteForm((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const handleInviteSubmit = (event) => {
    event.preventDefault();
    setInviteOpen(false);
  };

  const handleVisibilityChange = (map) => {
    setVisibility(map);
    saveVisibility(map);
  };

  const visible = new Set(visibility[role] || []);
  const roleLabel = ROLES.find((r) => r.id === role)?.label || role;
  /* ---------- GET /api/v1/agency/dashboard/overview ---------- */
  const [overview, setOverview] = useState({});

  const getOverview = async () => {
    try {
      const response = await GetAPIcalls('/api/v1/agency/dashboard/overview');
      if (response.status == 200) {
        setOverview(response.data);
      }
    } catch (err) {
      setOverview({});
      // showToast(err.message, 'error');
      console.error(err.message);
    }
  };
  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem('jwtToken');
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }
      return await response.json();
    } catch (error) {
      console.error('API Call Error:', error); // Log full error for debugging
      throw new Error(
        typeof error.message === 'string'
          ? error.message
          : 'Network error, please try again later',
      );
    }
  };
  useEffect(() => {
    getOverview();
  }, []);

  // Blue banner — API values, with the local helpers as fallback.
  const heroGreeting = overview?.greeting || greeting();
  // const heroDate = formatOverviewDate(overview?.today) || today();
  const heroDate = today();

  const openCases = overview?.open_cases || 0;
  return (
    <div className="min-h-screen bg-white rounded-2xl text-slate-900 font-sans lg:min-w-[78vw] lg:max-w-[80vw]">
      <main className="px-4 md:px-6 lg:px-8 pt-6 md:pt-8 pb-12 mx-auto space-y-4 md:space-y-5">
        {view === 'permissions' ? (
          <>
            <button
              onClick={() => setView('dashboard')}
              className="animate-fade-up inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 active:scale-[0.97]"
            >
              <ArrowLeft size={15} />
              Back to dashboard
            </button>
            <div className="animate-fade-up">
              <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                Role & Permissions
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Configure dashboard widget visibility for each role.
              </p>
            </div>
            <WidgetPermissions
              visibility={visibility}
              onChange={handleVisibilityChange}
            />
          </>
        ) : (
          /* ---------------- Dashboard (role-filtered) ---------------- */
          <div key={role} className="space-y-4 md:space-y-5">
            {/* ---------- Premium hero band ---------- */}
            <div className="animate-fade-up relative overflow-hidden rounded-2xl text-white shadow-lift bg-gradient-to-r from-[#1B2668] via-[#375AE7] to-[#375AE7] animate-gradient">
              {/* Ambient light */}
              <div className="absolute -top-24 right-8 w-72 h-72 rounded-full bg-white/10 blur-3xl animate-drift pointer-events-none" />
              <div className="absolute -bottom-32 -left-12 w-80 h-80 rounded-full bg-[#22D3EE]/20 blur-3xl animate-drift-slow pointer-events-none" />

              <div className="relative p-6 md:p-7 flex flex-col xl:flex-row xl:items-center gap-6">
                <div className="flex-1 min-w-0">
                  <h1
                    className="mt-1.5 text-2xl md:text-3xl font-bold tracking-tight"
                    data-api="overview.greeting"
                  >
                    {heroGreeting}
                  </h1>
                  <p
                    className="mt-1.5 text-sm text-white/75"
                    data-api="overview.today"
                  >
                    {heroDate}
                  </p>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur px-3 py-1.5 text-xs font-semibold text-white ring-1 ring-white/20 transition-transform duration-200 hover:scale-105"
                      data-api="overview.open_cases"
                    >
                      <FolderOpen size={13} className="text-[#7DD3FC]" />
                      {openCases} open cases
                    </span>
                    {/* <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EF4444]/20 backdrop-blur px-3 py-1.5 text-xs font-bold text-white ring-1 ring-[#EF4444]/40 transition-transform duration-200 hover:scale-105">
                      <AlarmClock
                        size={13}
                        className="text-[#FCA5A5] animate-pulse-dot"
                      />
                      1 breaching SLA
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#10B981]/20 backdrop-blur px-3 py-1.5 text-xs font-bold text-white ring-1 ring-[#10B981]/40 transition-transform duration-200 hover:scale-105">
                      <HeartPulse size={13} className="text-[#6EE7B7]" />
                      94% SLA health
                    </span> */}
                  </div>
                </div>

                {/* Controls — all high-contrast on the dark band */}
                <div className="flex flex-col sm:flex-row xl:flex-col gap-2.5 shrink-0">
                  <div className="flex gap-2.5">
                    <button
                      type="button"
                      onClick={() => setModalOpen(true)}
                      className="flex-1 inline-flex items-center justify-center gap-2 h-11 px-4 rounded-xl bg-white text-[#375AE7] text-sm font-bold shadow-md hover:bg-white/90 transition-all duration-200 active:scale-[0.97]"
                    >
                      <Plus size={15} />
                      Invite customer
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {visible.size === 0 ? (
              /* No widgets allowed for this role */
              <div className="animate-fade-up bg-white rounded-2xl border border-slate-200/70 shadow-card py-20 px-6 flex flex-col items-center justify-center text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-slate-100 mb-5">
                  <LayoutDashboard
                    className="w-6 h-6 text-slate-400"
                    strokeWidth={1.8}
                  />
                </div>
                <h3 className="text-base font-semibold text-slate-800">
                  No widgets enabled for this role
                </h3>
                <p className="text-sm text-slate-500 mt-2 max-w-md">
                  An administrator can enable dashboard widgets for{' '}
                  <span className="font-medium text-slate-700">
                    {roleLabel}
                  </span>{' '}
                  under widget settings.
                </p>
                <button
                  onClick={() => setView('permissions')}
                  className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-brand text-white text-sm font-semibold shadow-sm hover:bg-brand-dark transition-all duration-200 active:scale-[0.97]"
                >
                  <SlidersHorizontal size={15} />
                  Open widget settings
                </button>
              </div>
            ) : (
              <>
                {/* Needs your attention — directly below the greeting hero */}
                {visible.has('attention') && <AttentionCard />}

                {/* Onboarding and unwell sections */}
                {visible.has('funnel') && <FunnelCard />}

                {/* Other dashboard sections */}
                {visible.has('kpis') && <KpiGrid />}

                {/* =========================================================
                    RISK — COMMENTED OUT (code kept, not deleted).
                    The Risk Mix card and all risk-based layout gating are
                    disabled. Throughput now renders full-width.
                    Re-enable by un-commenting the block below.
                   ========================================================= */}
                {/* {(visible.has('throughput') || visible.has('riskMix')) && (
                  <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 md:gap-5">
                    {visible.has('throughput') && (
                      <div
                        className={
                          visible.has('riskMix')
                            ? 'xl:col-span-2'
                            : 'xl:col-span-3'
                        }
                      >
                        <ThroughputCard />
                      </div>
                    )}
                    {visible.has('riskMix') && <RiskMixCard />}
                  </div>
                )} */}

                {visible.has('throughput') && (
                  <div className="animate-fade-up">
                    <ThroughputCard />
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </main>

      <InviteCustomerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={addCustomer}
      />
    </div>
  );
}

function Field({ label, icon: Icon, required, error, children }) {
  return (
    <label className="block">
      <span className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 mb-1.5">
        {label}
        {required && <span className="text-brand">*</span>}
        {error && (
          <span className="text-red-500 font-medium ml-1">{error}</span>
        )}
      </span>
      {children}
    </label>
  );
}
/* Shared input styling so every field matches */
const inputCls = (error, extra = '') =>
  `w-full h-11 rounded-xl border bg-white pl-3.5 pr-3.5 text-sm text-slate-800 placeholder:text-slate-400 shadow-sm transition-all duration-200 focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand/10 ${
    error
      ? 'border-red-300 bg-red-50/40 focus:ring-red-100'
      : 'border-slate-200 hover:border-slate-300 focus:bg-white'
  } ${extra}`;
function SectionLabel({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2 mb-2.5">
      <span className="w-5 h-5 rounded-md bg-brand/10 flex items-center justify-center text-brand">
        <Icon size={11} strokeWidth={2.2} />
      </span>
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
        {title}
      </span>
      <span className="h-px flex-1 bg-slate-100" />
    </div>
  );
}
